import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { login, safeReturnTo, signUp } from '../auth'
import { ConfirmCode, Field, PASSWORD_HINT } from '../components/AuthParts'
import { ProblemBanner } from '../components/Problem'

/** Create an account: details, then the emailed code, then straight in. */
export default function SignUp({ onSignedIn }: { onSignedIn: () => void }) {
  const [parameters] = useSearchParams()
  const navigate = useNavigate()
  const returnTo = safeReturnTo(parameters.get('returnTo'))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  if (confirming) {
    return (
      <div className="auth">
        <ConfirmCode
          email={email.trim()}
          onConfirmed={async () => {
            // The password is still in this page's state, so no second prompt is needed.
            const result = await login(email.trim(), password)
            if (result.state === 'signed-in') {
              onSignedIn()
              navigate(returnTo, { replace: true })
            } else {
              navigate('/login', { replace: true })
            }
          }}
        />
      </div>
    )
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    signUp(email.trim(), password)
      .then(() => setConfirming(true))
      .catch(setError)
      .finally(() => setBusy(false))
  }

  return (
    <div className="auth">
      <form className="auth-form" onSubmit={submit}>
        <h1>Create an account</h1>
        <ProblemBanner error={error} />
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          required
          autoFocus
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          hint={PASSWORD_HINT}
          required
        />
        <div className="row">
          <button className="primary" type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create account'}
          </button>
        </div>
        <p className="muted">
          By creating an account you agree to the <Link to="/terms">terms of service</Link> and
          have read the <Link to="/privacy">privacy notice</Link>.
        </p>
        <p className="muted">
          Already have an account?{' '}
          <Link to={{ pathname: '/login', search: parameters.toString() }}>Log in</Link>
        </p>
      </form>
    </div>
  )
}
